import type { FC } from "react";
import {
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioPaymentFrequencyEnum,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
	CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { ToolIdentificationRow } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolIdentificationRow";
import { ToolSummaryRows } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolSummaryRows";
import { ToolTypeRow } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolTypeRow";
import { getCommodityLabel } from "web_myplann/Portfolio/Forms/ToolsShared/getCommodityLabel";
import { toolHasActivePeriodicPurchaseInstructions } from "web_myplann/Portfolio/Helpers/activePurchaseInstructions";
import { getCryptoHoldingTypeLabel } from "web_myplann/Portfolio/Helpers/getCryptoHoldingTypeLabel";
import {
	type NormalizedTool,
	ToolKind,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

const PageResources = Resources.PortfolioDetail;

type Props = {
	tool: NormalizedTool;
	currencySign: string;
	totalValue: string;
	totalInvested: string;
};

export const ToolFieldRows: FC<Props> = ({
	tool,
	currencySign,
	totalValue,
	totalInvested,
}) => {
	const { t } = useResource();

	const toolType = tool.kind;
	const showRecurringPayment =
		toolHasActivePeriodicPurchaseInstructions(tool) && tool.monthlyPayment > 0;

	const monthlyRecurringValue = showRecurringPayment
		? `${moneyFormatter(
				tool.monthlyPayment,
				t(PageResources.SharedLabels.Currency[CurrencyEnum.CZK]),
				undefined,
				0,
			)} ${t(
				PageResources.ToolHistory.Fund.Frequency[
					ClientPortfolioInvestmentFrequencyEnum.Monthly
				],
			).toLowerCase()}`
		: null;

	if (toolType === ToolKind.Investment) {
		return (
			<>
				<ToolTypeRow tool={tool} />
				<ToolIdentificationRow tool={tool} />
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	if (toolType === ToolKind.Commodity) {
		return (
			<>
				<ToolTypeRow
					tool={{ type: tool.type ?? ClientPortfolioToolTypeEnum.Commodity }}
				/>
				<DetailRow
					label={t(PageResources.ToolHistory.Commodity.name)}
					value={getCommodityLabel(tool.identification ?? "", t)}
				/>
				<ToolSummaryRows
					recurringValue={
						showRecurringPayment
							? getPortfolioFrequencyValue(
									tool.monthlyPayment,
									currencySign,
									ClientPortfolioPaymentFrequencyEnum.Monthly,
									t,
									0,
								)
							: null
					}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	if (toolType === ToolKind.Crypto) {
		const holdingTypeLabel = getCryptoHoldingTypeLabel(tool, t);
		return (
			<>
				<ToolTypeRow
					tool={{ type: tool.type ?? ClientPortfolioToolTypeEnum.Crypto }}
				/>
				<DetailRow
					label={t(PageResources.ToolHistory.Cryptocurrency.name)}
					value={tool.identification}
				/>
				{holdingTypeLabel && (
					<DetailRow
						label={t(PageResources.ToolHistory.Cryptocurrency.holdingType)}
						value={holdingTypeLabel}
					/>
				)}
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	if (toolType === ToolKind.BankProduct) {
		return (
			<>
				<ToolTypeRow
					tool={{ type: tool.type ?? ClientPortfolioToolTypeEnum.BankProduct }}
				/>
				{tool.identification && (
					<DetailRow
						label={t(
							PageResources.LifeInsurance.InvestmentTool.IdentificationType[
								ClientPortfolioToolIdentificationTypeEnum.Other
							],
						)}
						value={tool.identification}
					/>
				)}
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	return (
		<>
			<ToolTypeRow tool={tool} />
			<ToolIdentificationRow tool={tool} />
			<ToolSummaryRows
				recurringValue={null}
				totalInvested={totalInvested}
				totalValue={totalValue}
			/>
		</>
	);
};
